import React, { useMemo, useState } from "react";

import {
	Badge,
	Button,
	Card,
	ConfirmModal,
	NumberInput,
	Select,
	Table,
	TTableColumn,
} from "@amphore-dev/amphore-lib";
import { format } from "date-fns";
import { t } from "i18next";
import { useNavigate, useParams } from "react-router-dom";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import AdminService from "@/services/AdminService";

import { usePlans } from "@/hooks/useSubscription";

import { UsageGauges } from "@/components/molecules/UsageGauges/UsageGauges";
import { PageLayout } from "@/components/templates";

import { subscriptionStatusLine } from "@/utils/USubscription";

import { IS_PROD } from "@/constants/CEnv";
import { PLAN_NAMES } from "@/constants/CPlans";
import { adminKeys, subscriptionKeys } from "@/constants/CQueryKeys";

import { TAdminStripeEvent } from "@/types/TAdmin";
import { TPlanCode } from "@/types/TSubscription";

import { promiseToast } from "@/modules/index";

/** Every plan a hand can grant: the public ones plus enterprise, which is never sold online. */
const GRANTABLE: TPlanCode[] = [
	"free",
	"user",
	"user_plus",
	"team",
	"team_plus",
	"enterprise",
];

const DEFAULT_TRIAL_DAYS = 14;

/** The Stripe dashboard, test or live to match the API's keys. */
const stripeCustomerUrl = (customerId: string) =>
	`https://dashboard.stripe.com/${IS_PROD ? "" : "test/"}customers/${customerId}`;

const formatDate = (iso: string | null) =>
	iso ? format(new Date(iso), "dd/MM/yyyy") : "—";

/**
 * /admin/workspaces/:id — one workspace in full: who is in it, where its
 * subscription stands with the Stripe ids behind it, how full each limit
 * is, the webhook deliveries about its customer, and the three things an
 * admin can do to it: grant a plan by hand, add days to the trial, pull
 * Stripe's state over ours. The accounts list links here.
 */
export const AdminWorkspaceDetail: React.FC = () => {
	const { id: idParam } = useParams<{ id: string }>();
	const id = Number(idParam);
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const plans = usePlans();

	const [pendingPlan, setPendingPlan] = useState<TPlanCode | null>(null);
	const [trialDays, setTrialDays] = useState<number | null>(
		DEFAULT_TRIAL_DAYS
	);

	const {
		data: workspace,
		isLoading,
		error,
	} = useQuery({
		queryKey: adminKeys.workspace(id),
		queryFn: () => AdminService.workspace(id),
		enabled: Number.isInteger(id) && id > 0,
	});

	const invalidate = () => {
		void queryClient.invalidateQueries({ queryKey: adminKeys.all });
		// The admin may be looking at their own account.
		void queryClient.invalidateQueries({
			queryKey: subscriptionKeys.current,
		});
	};

	const setPlan = useMutation({
		mutationFn: (plan: TPlanCode) => AdminService.setPlan(id, plan),
		onSuccess: () => {
			setPendingPlan(null);
			invalidate();
		},
	});
	const extendTrial = useMutation({
		mutationFn: (days: number) => AdminService.extendTrial(id, days),
		onSuccess: invalidate,
	});
	const reconcile = useMutation({
		mutationFn: () => AdminService.reconcileWorkspace(id),
		onSuccess: invalidate,
	});

	// The toasts show the API's own message on failure: the 409s say
	// what stands in the way (a live Stripe subscription).
	const confirmPlan = async () => {
		if (!pendingPlan) return;
		const prom = setPlan.mutateAsync(pendingPlan);
		promiseToast(prom);
		await prom.catch(() => setPendingPlan(null));
	};
	const runExtend = () => {
		if (!trialDays || trialDays < 1) return;
		promiseToast(extendTrial.mutateAsync(trialDays), {
			success: t("admin.user.trialExtended", { count: trialDays }),
		});
	};
	const runReconcile = () => {
		promiseToast(reconcile.mutateAsync(), {
			success: t("admin.user.reconciled"),
		});
	};

	const planOptions = useMemo(
		() =>
			GRANTABLE.map((code) => ({
				value: code,
				label:
					plans.data?.find((plan) => plan.code === code)?.name ??
					PLAN_NAMES[code],
			})),
		[plans.data]
	);

	const eventColumns: TTableColumn<TAdminStripeEvent>[] = useMemo(
		() => [
			{
				key: "receivedAt",
				label: t("admin.billing.columns.received"),
				render: (event) =>
					format(new Date(event.receivedAt), "dd/MM/yyyy HH:mm:ss"),
			},
			{
				key: "type",
				label: t("admin.billing.columns.type"),
				width: "1fr",
				render: (event) => (
					<span className="font-mono text-sm">{event.type}</span>
				),
			},
			{
				key: "error",
				label: t("admin.billing.columns.status"),
				truncate: false,
				render: (event) =>
					event.error ? (
						<Badge
							color="danger"
							variant="tint"
							size="sm"
							title={event.error}
						>
							{t("admin.billing.status.failed")}
						</Badge>
					) : (
						<Badge color="success" variant="tint" size="sm">
							{t("admin.billing.status.processed")}
						</Badge>
					),
			},
		],
		[]
	);

	const subscription = workspace?.subscription ?? null;
	const owner = workspace?.owner;
	const status = workspace
		? subscriptionStatusLine(subscription, workspace.plan)
		: null;
	const stripeLive =
		!!subscription?.hasCustomer &&
		subscription.status !== "canceled" &&
		subscription.status !== "expired";

	return (
		<PageLayout
			// A solo workspace has no name: it is the person who owns it.
			title={
				workspace
					? (workspace.name ??
						`${owner?.firstName ?? ""} ${owner?.lastName ?? ""}`.trim())
					: t("admin.user.title")
			}
			isLoading={isLoading}
			error={!!error || (!isLoading && !workspace)}
			onBack={() => navigate("/admin/users")}
			onBackLabel={t("admin.user.back")}
		>
			{workspace && owner && status && (
				<div className="flex flex-col gap-4">
					<Card className="flex flex-col gap-3">
						<div className="flex flex-wrap items-center gap-2">
							<span className="text-neutral-500">
								@{owner.username}
							</span>
							<span className="text-neutral-500">
								{owner.mail}
							</span>
							<Badge color="neutral" variant="outline" size="sm">
								{t("admin.workspace.members", {
									count: workspace.members.length,
								})}
							</Badge>
						</div>
						<div className="flex flex-wrap items-center gap-2">
							<Badge color="primary" variant="tint">
								{PLAN_NAMES[workspace.plan]}
							</Badge>
							<Badge
								color={status.color}
								variant="tint"
								data-testid="admin-user-status"
							>
								{status.text}
							</Badge>
							{subscription &&
								subscription.plan !== workspace.plan && (
									<span className="text-sm text-neutral-500">
										{t("admin.user.subscribedTo", {
											plan: PLAN_NAMES[subscription.plan],
										})}
									</span>
								)}
						</div>
						<dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2 xl:grid-cols-4">
							<Field
								label={t("admin.users.columns.since")}
								value={formatDate(workspace.createdAt)}
							/>
							<Field
								label={t("admin.user.trialEndsAt")}
								value={formatDate(
									subscription?.trialEndsAt ?? null
								)}
							/>
							<Field
								label={t("admin.user.periodEnd")}
								value={
									subscription?.currentPeriodEnd
										? `${formatDate(subscription.currentPeriodEnd)}${
												subscription.cancelAtPeriodEnd
													? ` · ${t("admin.user.cancelScheduled")}`
													: ""
											}`
										: "—"
								}
							/>
							<Field
								label={t("admin.user.customer")}
								value={
									workspace.stripeCustomerId ? (
										<a
											href={stripeCustomerUrl(
												workspace.stripeCustomerId
											)}
											target="_blank"
											rel="noopener noreferrer"
											className="font-mono underline"
											data-testid="admin-user-stripe-link"
										>
											{workspace.stripeCustomerId}
										</a>
									) : (
										"—"
									)
								}
							/>
							<Field
								label={t("admin.user.subscription")}
								value={
									<span className="font-mono">
										{workspace.stripeSubscriptionId ?? "—"}
									</span>
								}
							/>
							<Field
								label={t("admin.user.price")}
								value={
									<span className="font-mono">
										{workspace.stripePriceId ?? "—"}
									</span>
								}
							/>
						</dl>
					</Card>

					<Card className="flex flex-col gap-3">
						<h2 className="text-sm font-medium">
							{t("subscription.usage.title")}
						</h2>
						<UsageGauges usage={workspace.usage} />
					</Card>

					<Card className="flex flex-col gap-4">
						<h2 className="text-sm font-medium">
							{t("admin.user.actions")}
						</h2>
						<div className="grid gap-4 md:grid-cols-3">
							<div className="flex flex-col gap-2">
								<span className="text-sm text-neutral-500">
									{t("admin.users.columns.grant")}
								</span>
								<Select<TPlanCode>
									options={planOptions}
									value={workspace.plan}
									size="sm"
									isClearable={false}
									searchable={false}
									aria-label={t("admin.users.columns.grant")}
									// A live Stripe subscription is the portal's
									// to change; the API would refuse anyway.
									disabled={stripeLive}
									onChange={(value) => {
										if (
											typeof value === "string" &&
											value !== workspace.plan
										) {
											setPendingPlan(value);
										}
									}}
								/>
							</div>
							<div className="flex flex-col gap-2">
								<span className="text-sm text-neutral-500">
									{t("admin.user.extendTrial")}
								</span>
								<div className="flex items-center gap-2">
									<NumberInput
										value={trialDays}
										onChange={setTrialDays}
										min={1}
										max={365}
										size="sm"
										aria-label={t("admin.user.days")}
										disabled={stripeLive}
										className="w-28"
									/>
									<Button
										size="sm"
										variant="outline"
										picto="clock"
										onClick={runExtend}
										disabled={stripeLive || !trialDays}
										isLoading={extendTrial.isPending}
										data-testid="admin-user-extend"
									>
										{t("admin.user.extend")}
									</Button>
								</div>
								{stripeLive && (
									<span className="text-xs text-neutral-500">
										{t("admin.user.stripeOwns")}
									</span>
								)}
							</div>
							<div className="flex flex-col gap-2">
								<span className="text-sm text-neutral-500">
									{t("admin.user.reconcile")}
								</span>
								<Button
									size="sm"
									variant="outline"
									picto="refresh"
									onClick={runReconcile}
									disabled={!workspace.stripeSubscriptionId}
									isLoading={reconcile.isPending}
									data-testid="admin-user-reconcile"
								>
									{t("admin.user.reconcileNow")}
								</Button>
								{!workspace.stripeSubscriptionId && (
									<span className="text-xs text-neutral-500">
										{t("admin.user.noStripe")}
									</span>
								)}
							</div>
						</div>
					</Card>

					<Card className="flex flex-col gap-3">
						<h2 className="text-sm font-medium">
							{t("admin.user.events")}
						</h2>
						<Table
							items={workspace.events}
							getItemKey={(event) => event.id}
							columns={eventColumns}
							noDataMessage={t("admin.user.noEvents")}
						/>
					</Card>
				</div>
			)}
			<ConfirmModal
				open={!!pendingPlan}
				onClose={() => setPendingPlan(null)}
				onConfirm={confirmPlan}
				title={t("admin.users.confirm.title")}
				confirmText={t("admin.users.confirm.submit")}
				cancelText={t("actions.cancel")}
				size="sm"
			>
				{workspace && pendingPlan && (
					<p data-testid="admin-user-confirm">
						{t("admin.users.confirm.message", {
							name:
								workspace.name ??
								`${workspace.owner.firstName} ${workspace.owner.lastName}`,
							from: PLAN_NAMES[workspace.plan],
							to: PLAN_NAMES[pendingPlan],
						})}
					</p>
				)}
			</ConfirmModal>
		</PageLayout>
	);
};

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({
	label,
	value,
}) => (
	<div className="flex flex-col">
		<dt className="text-xs text-neutral-500">{label}</dt>
		<dd>{value}</dd>
	</div>
);
